import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRightIcon, CopyIcon, LoaderIcon, MessageCircleIcon } from 'lucide-react';
const WAITLIST_URL = 'https://script.google.com/macros/s/AKfycbwh0J9Vkq6zT5v5-Pe-5GZczrP-7RSoI2ANR30Epc1TUMBa6CtWBK-Hu7W5Kn1EZVlOfw/exec';
const SITE_URL = 'https://www.kittypot.com';
const WHATSAPP_SHARE_URL = `https://wa.me/?text=${encodeURIComponent('KittyPot makes it simple to track shared expenses and settle up. Join the early-access waitlist: https://www.kittypot.com')}`;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function FinalCTA() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const hasSubmittedRef = useRef(false);
  const isInitialIframeLoadRef = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetStatusRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (resetStatusRef.current) clearTimeout(resetStatusRef.current);
    };
  }, []);
  const clearTimers = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (resetStatusRef.current) clearTimeout(resetStatusRef.current);
  };
  const showError = (message: string) => {
    clearTimers();
    hasSubmittedRef.current = false;
    setErrorMessage(message);
    setStatus('error');
    resetStatusRef.current = setTimeout(() => setStatus('idle'), 4000);
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (status === 'loading') {
      e.preventDefault();
      return;
    }
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName || !trimmedEmail || !EMAIL_PATTERN.test(trimmedEmail)) {
      e.preventDefault();
      showError(!trimmedName ? 'Please enter your full name.' : 'Please enter a valid email address.');
      return;
    }
    const form = e.currentTarget;
    const nameInput = form.elements.namedItem('name') as HTMLInputElement;
    const emailInput = form.elements.namedItem('email') as HTMLInputElement;
    nameInput.value = trimmedName;
    emailInput.value = trimmedEmail;
    setName(trimmedName);
    setEmail(trimmedEmail);
    clearTimers();
    setErrorMessage('');
    hasSubmittedRef.current = true;
    setStatus('loading');
    timeoutRef.current = setTimeout(() => {
      showError('We couldn’t submit your details. Please try again.');
    }, 8000);
  };
  const handleIframeLoad = () => {
    if (isInitialIframeLoadRef.current) {
      isInitialIframeLoadRef.current = false;
      return;
    }
    if (!hasSubmittedRef.current) return;
    clearTimers();
    hasSubmittedRef.current = false;
    setStatus('success');
    setName('');
    setEmail('');
    resetStatusRef.current = setTimeout(() => setStatus('idle'), 5000);
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(SITE_URL);
      setIsLinkCopied(true);
      window.setTimeout(() => setIsLinkCopied(false), 2000);
    } catch {
      window.prompt('Copy this link:', SITE_URL);
    }
  };
  return <section id="waitlist" className="relative py-24 lg:py-32 bg-gray-950 overflow-hidden" data-id="element-17" data-mp="3fd3ju6tcmajo">
      {/* Background glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.08),transparent_60%)]" data-id="element-18" data-mp="3kd94h317aws9" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-19" data-mp="36ejahw9ohd84">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} data-id="element-20" data-mp="3bcb9194tvxla">
          <div className="bg-white p-2 rounded-2xl w-24 h-24 mx-auto mb-6 flex items-center justify-center" data-id="element-21" data-mp="35njhiu3fy1qi">
            <img src="https://cdn.magicpatterns.com/uploads/vpaWAD2nLEfW1hoAd36jz7/LPot_only.png" alt="KittyPot logo" className="w-16 h-16 object-contain" data-id="element-22" data-mp="30nyfgasz5d9k" />
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-5" data-id="element-23" data-mp="2vr805pozulp7">
            Track together.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-kitty-yellow-400 to-orange-400" data-id="element-24" data-mp="2mpqm0cv5cu35">
              
Settle clearly.
            </span>
          </h2>
          <p className="text-lg lg:text-xl text-gray-400 mb-10 max-w-lg mx-auto" data-id="element-25" data-mp="2cycg9nclqy1k">
            Join KittyPot's early-access waitlist for first access and product
            updates.
          </p>

          <iframe name="waitlist_target" title="Waitlist submission" src="about:blank" onLoad={handleIframeLoad} className="hidden" data-id="element-26" data-mp="23ype2zc1to7g" />

          {status === 'success' ? <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-lg mx-auto mb-6 bg-green-500/10 border border-green-500/20 rounded-2xl px-6 py-4 flex items-center justify-center gap-3" data-id="element-27" data-mp="1z17fo2vnqrdp">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 self-start mt-1" data-id="element-28" data-mp="ni9ua5oxc1gw" />
              <div className="text-left flex-1" data-id="element-29" data-mp="sgbe4su6c4tf">
                <p className="text-green-300 font-semibold" data-id="element-30" data-mp="3rw8clowmffoe">
                  You're on the waitlist 🎉
                </p>
                <p className="text-green-400/60 text-sm mt-1" data-id="element-31" data-mp="3vwsc2z4o2vdp">
                  We'll keep you posted on your early-access invitation and
                  product updates.
                </p>
                <div className="mt-4 border-t border-green-400/15 pt-3" data-id="element-32" data-mp="9i8uktkeb030">
                  <p className="text-sm font-medium text-green-200" data-id="element-33" data-mp="387z94ipmnn9l">
                    Know someone who’d love this? Share KittyPot
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2" data-id="element-34" data-mp="3d5hmqmelaqnx">
                    <a href={WHATSAPP_SHARE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-green-400/10 px-3 py-1.5 text-xs font-semibold text-green-200 transition-colors hover:bg-green-400/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-35" data-mp="kc2bjxuhvgdp">
                      <MessageCircleIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-36" data-mp="p4lkghi8lnc6" />
                      WhatsApp
                    </a>
                    <button type="button" onClick={handleCopyLink} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-green-200 transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-37" data-mp="1fkhh9s6b4ea">
                      <CopyIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-38" data-mp="5hnw27a52p3s" />
                      {isLinkCopied ? 'Link copied' : 'Copy link'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div> : <form method="POST" action={WAITLIST_URL} target="waitlist_target" noValidate onSubmit={handleSubmit} className="max-w-lg mx-auto mb-6 space-y-4" data-id="element-39" data-mp="1pafkta3b3w49">
              <input type="text" name="name" placeholder="Full name" value={name} onChange={e => setName(e.target.value)} className="w-full pl-5 pr-5 py-[14px] lg:py-4 min-h-[52px] rounded-full border border-white/20 bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-kitty-yellow focus:border-transparent text-white placeholder-gray-400 text-base backdrop-blur-sm" data-id="element-40" data-mp="1fdazfchac4oi" />
              <input type="email" name="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} className="w-full pl-5 pr-5 py-[14px] lg:py-4 min-h-[52px] rounded-full border border-white/20 bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-kitty-yellow focus:border-transparent text-white placeholder-gray-400 text-base backdrop-blur-sm" data-id="element-41" data-mp="2d35utdrng5bw" />
              <button type="submit" disabled={status === 'loading'} className="w-full bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-[16px] lg:py-4 min-h-[56px] rounded-full font-bold transition-all active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-base disabled:opacity-70 disabled:cursor-not-allowed mt-2" data-id="element-42" data-mp="2vsvpm0vewr3t">
                {status === 'loading' ? <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-43" data-mp="hzhojzj6d4m5" /> : <>
                    Join the waitlist
                    <ArrowRightIcon className="w-4 h-4" data-id="element-44" data-mp="1ene3z12vltvs" />
                  </>}
              </button>
              <p className="text-xs text-gray-500" data-id="element-45" data-mp="19pvsb2xh50k3">
                By joining you agree to our{' '}
                <a href="/privacy-policy" className="font-semibold text-gray-300 underline underline-offset-2 transition-colors hover:text-white" data-id="element-46" data-mp="15lo6pembc0q3">
                  Privacy Policy
                </a>
                .
              </p>
              {status === 'error' && <p className="text-red-400 text-sm" role="alert" aria-live="polite" data-id="element-47" data-mp="3ledjrsx795xh">
                  {errorMessage}
                </p>}
            </form>}

          {/* Early access positioning */}
          <div className="flex flex-col items-center gap-4" data-id="element-48" data-mp="25uw9tgjykkq5">
            <p className="text-sm text-gray-500" data-id="element-49" data-mp="29xapxsw8t6dq">
              Product updates and early-access news, straight to your inbox.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}